// The single source of the app's motion: every duration, curve and ink colour
// lives here so a moment can be retuned in one place instead of in eight
// components. See docs/superpowers/specs/2026-07-31-momenty-a-prechody-design.md

/** How much movement the user asked for (Nastavenia -> Efekty a animácie). */
export type MotionLevel = 'full' | 'reduced' | 'off';

/**
 * What an action MEANS, not what colour it is. Only 'project' carries a colour
 * from outside — the running timer wears the colour of its project.
 */
export type InkTone = 'create' | 'done' | 'bug' | 'blocked' | 'warn' | 'project';

export const BRAND = '#007aff';

const TONE_COLOR: Record<Exclude<InkTone, 'project'>, string> = {
  create: BRAND,
  done: '#34c759',
  bug: '#ff453a',
  blocked: '#bf5af2',
  warn: '#ff9f0a',
};

export const inkColor = (tone: InkTone, projectColor?: string | null): string =>
  tone === 'project' ? projectColor || BRAND : TONE_COLOR[tone];

/** Motion budget from the spec, in milliseconds. */
export const DURATION = {
  /** The colour has to cross a 27" screen and still feel unhurried. */
  ink: 900,
  sessionStart: 780,
  receiptIn: 420,
  receiptHold: 3500,
  receiptOut: 520,
  morph: 300,
  fly: 620,
  count: 900,
  greeting: 1200,
} as const;

export const EASE = {
  /** Arrivals: fast start, long soft landing. */
  out: 'cubic-bezier(.22, 1, .36, 1)',
  /** Things that settle into place (sheets, heroes, flights). */
  settle: 'cubic-bezier(.32, .72, 0, 1)',
} as const;

/**
 * 'reduced' keeps the choreography readable but gets out of the way; 'off'
 * means the change is simply there, with no animation at all.
 */
export const scaleDuration = (ms: number, level: MotionLevel): number => {
  if (level === 'off') {
    return 0;
  }
  return level === 'reduced' ? Math.round(ms * 0.4) : ms;
};

export const systemPrefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;
